/**
 * `safety` — the life-safety bypass (gas, fire, active flooding) and the
 * low-confidence rule. Distinct from routing urgency: this screen can never be
 * dismissed (`emergency_dismissible` is fixed false by the contract).
 */
import { DEFAULT_EMERGENCY_MESSAGE } from '../../../../diagnosis-widget/src/defaultCopy';
import { Field } from '../../ui/kit';
import { FORMATTING_HINT, NullableText, PathSelect, PathSwitch, SectionCard, usePath } from './common';

export function SafetySection() {
  const phone = usePath<string | null>('safety.emergency_phone');
  const threshold = usePath<number>('safety.low_confidence_threshold');
  const pct = Math.round(threshold.value * 100);
  return (
    <SectionCard id="sec-safety" title="Safety" description="What happens when a problem is dangerous, or when the diagnosis is unsure.">
      <PathSwitch
        path="safety.emergency_bypass"
        label="Emergency bypass"
        description="Gas, fire and active flooding skip the diagnosis and go straight to safety instructions."
      />
      <div className="notice info">
        <span>The emergency screen can never be dismissed by the visitor. That is fixed, not a setting.</span>
      </div>
      <NullableText path="safety.emergency_message_md" label="Emergency message" multiline placeholder={DEFAULT_EMERGENCY_MESSAGE} hint={FORMATTING_HINT} />
      <Field
        label="Your emergency line"
        hint="International format, e.g. +15550102000. Leave empty and the screen shows 911 only."
        issues={phone.issues}
        optional
      >
        {(p) => (
          <input
            {...p}
            className="input input-mono"
            inputMode="tel"
            value={phone.value ?? ''}
            placeholder="+15550102000"
            onChange={(e) => phone.set(e.target.value.trim() === '' ? null : e.target.value.replace(/[\s()-]/g, ''))}
          />
        )}
      </Field>
      <hr className="divider" />
      <Field label={`Low-confidence threshold: ${pct}%`} hint="Diagnoses less sure than this follow the rule below." issues={threshold.issues}>
        {(p) => (
          <input
            {...p}
            type="range"
            min={0}
            max={100}
            step={5}
            value={pct}
            aria-valuetext={`${pct} percent`}
            onChange={(e) => threshold.set(Number(e.target.value) / 100)}
          />
        )}
      </Field>
      <PathSelect
        path="safety.low_confidence_behavior"
        label="When the diagnosis is unsure"
        options={[
          { value: 'capture_anyway', label: 'Take the request anyway' },
          { value: 'human_escalation', label: 'Hand it to a person on my team' },
          { value: 'message_only', label: 'Show a message only' },
        ]}
      />
    </SectionCard>
  );
}
